/**
 * Centralized user-friendly error messages
 * All error messages should be human-readable and actionable
 */

export const ERROR_MESSAGES = {
  // Authentication Errors
  auth: {
    invalidCredentials: 'The email or password you entered is incorrect. Please check and try again.',
    emailNotFound: 'No account found with this email address. Please sign up first.',
    emailAlreadyExists: 'An account with this email already exists. Try signing in instead.',
    userNotFound: 'Account not found. Please check your email or create a new account.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    notAuthenticated: 'You need to sign in to access this feature.',
    passwordWeak: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.',
    passwordMismatch: 'Passwords do not match. Please try again.',
    googleNotConfigured: 'Google sign-in is not configured. Please contact support or use email sign-in.',
    redirectError: 'There was a problem with the redirect. Please try again.',
  },

  // OTP Errors
  otp: {
    expired: 'Verification code has expired. Please request a new one.',
    invalid: 'Invalid verification code. Please check and try again.',
    maxAttempts: 'Too many failed attempts. Please wait 10 minutes before trying again.',
    sendFailed: 'Failed to send verification code. Please try again.',
    tooManyRequests: 'Too many code requests. Please wait 10 minutes before requesting another.',
  },

  // Database Errors
  database: {
    connectionFailed: 'Unable to connect to the server. Please check your internet connection and try again.',
    queryFailed: 'Something went wrong while fetching data. Please refresh and try again.',
    saveFailed: 'Failed to save your changes. Please try again.',
    deleteFailed: 'Failed to delete. Please try again.',
    duplicateRecord: 'This record already exists. Please use a different name or identifier.',
    permissionDenied: 'You don\'t have permission to perform this action.',
    notFound: 'The requested data could not be found.',
    timeout: 'The operation took too long. Please try again.',
  },

  // Form Validation Errors
  form: {
    required: (field: string) => `${field} is required.`,
    minLength: (field: string, min: number) => `${field} must be at least ${min} characters.`,
    maxLength: (field: string, max: number) => `${field} must be less than ${max} characters.`,
    invalidEmail: 'Please enter a valid email address.',
    invalidPhone: 'Please enter a valid phone number.',
    invalidNumber: (field: string) => `${field} must be a valid number.`,
    positiveNumber: (field: string) => `${field} must be greater than 0.`,
    invalidDate: 'Please enter a valid date.',
    invalidFormat: (field: string, expected: string) => `${field} format is invalid. Expected: ${expected}`,
  },

  // File Upload Errors
  upload: {
    fileTooLarge: (maxSize: string) => `File size exceeds the maximum limit (${maxSize}). Please choose a smaller file.`,
    invalidFileType: (allowedTypes: string) => `Invalid file format. Please upload ${allowedTypes} files only.`,
    uploadFailed: 'File upload failed. Please try again.',
    parseError: 'Could not read the file. Please make sure it\'s in the correct format.',
    emptyFile: 'The uploaded file is empty. Please select a file with data.',
    missingColumns: (columns: string[]) => `Missing required columns: ${columns.join(', ')}`,
    invalidData: (row: number, reason: string) => `Row ${row}: ${reason}`,
  },

  // Business Logic Errors
  business: {
    insufficientStock: (product: string, available: number) => 
      `Insufficient stock for ${product}. Only ${available} available.`,
    invoiceAlreadyPaid: 'This invoice has already been fully paid.',
    invalidAmount: 'The amount entered is invalid. Please check and try again.',
    cannotEdit: 'This record cannot be edited at this time.',
    cannotDelete: 'This record cannot be deleted. It may be referenced by other records.',
    ledgerCorrupted: 'Ledger data inconsistency detected. Please contact support for assistance.',
    organizationNotFound: 'Your organization could not be found. Please sign out and sign in again.',
  },

  // Network Errors
  network: {
    offline: 'You appear to be offline. Please check your internet connection.',
    timeout: 'The request took too long. Please check your connection and try again.',
    serverError: 'Server error occurred. Our team has been notified. Please try again later.',
    serviceUnavailable: 'Service temporarily unavailable. Please try again in a few minutes.',
  },

  // Generic Errors
  generic: {
    unknown: 'Something went wrong. Please try again.',
    tryAgain: 'An error occurred. Please try again.',
    contactSupport: 'An unexpected error occurred. Please contact support if this continues.',
  },
};

/**
 * Get a user-friendly error message based on error code or type
 */
export function getUserFriendlyMessage(error: any): string {
  if (!error) return ERROR_MESSAGES.generic.unknown;

  const message = error.message?.toLowerCase() || '';
  const code = error.code;

  // Check for specific error patterns
  if (message.includes('invalid login') || message.includes('invalid credential') || code === 1001) {
    return ERROR_MESSAGES.auth.invalidCredentials;
  }
  
  if (message.includes('user not found') || message.includes('no user') || code === 1003) {
    return ERROR_MESSAGES.auth.userNotFound;
  }
  
  if (message.includes('email already') || message.includes('already registered') || code === 1002) {
    return ERROR_MESSAGES.auth.emailAlreadyExists;
  }
  
  if (message.includes('session expired') || code === 1004) {
    return ERROR_MESSAGES.auth.sessionExpired;
  }
  
  if (message.includes('otp expired') || message.includes('code expired') || code === 2001) {
    return ERROR_MESSAGES.otp.expired;
  }
  
  if (message.includes('otp invalid') || message.includes('invalid code') || message.includes('invalid verification') || code === 2002) {
    return ERROR_MESSAGES.otp.invalid;
  }
  
  if (message.includes('too many') || message.includes('rate limit') || code === 2003 || code === 5001) {
    return ERROR_MESSAGES.otp.maxAttempts;
  }
  
  if (message.includes('duplicate') || message.includes('already exists') || code === '23505') {
    return ERROR_MESSAGES.database.duplicateRecord;
  }
  
  if (message.includes('permission') || message.includes('policy') || code === '42501' || code === 3003) {
    return ERROR_MESSAGES.database.permissionDenied;
  }
  
  if (message.includes('connection') || message.includes('network') || code === 3001) {
    return ERROR_MESSAGES.database.connectionFailed;
  }
  
  if (message.includes('timeout')) {
    return ERROR_MESSAGES.network.timeout;
  }
  
  if (message.includes('not authenticated') || message.includes('unauthorized')) {
    return ERROR_MESSAGES.auth.notAuthenticated;
  }

  if (message.includes('organization not found')) {
    return ERROR_MESSAGES.business.organizationNotFound;
  }

  if (message.includes('insufficient stock')) {
    return message; // Already user-friendly
  }

  if (message.includes('ledger') && (message.includes('corrupt') || message.includes('integrity'))) {
    return ERROR_MESSAGES.business.ledgerCorrupted;
  }

  // Return the original message if it seems user-friendly (doesn't contain technical terms)
  if (error.message && !message.includes('error') && !message.includes('exception') && 
      !message.includes('failed') && message.length < 200) {
    return error.message;
  }

  return ERROR_MESSAGES.generic.unknown;
}
